import { useState } from "react";
import axios from "axios";
import { GraduationCap, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { IMAGES, CONTACT } from "@/data/site";
import { Reveal } from "./Reveal";

// Envío sin backend: usa FormSubmit.co (gratuito, sin registro) para
// reenviar el lead directo al correo del negocio.
const FORM_ENDPOINT = `https://formsubmit.co/ajax/${encodeURIComponent(CONTACT.email)}`;

export const Academy = () => {
  const { t, lang } = useLang();
  const a = t.academy;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      toast.error(a.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        FORM_ENDPOINT,
        {
          email,
          idioma: lang,
          _subject: `Nuevo lead (Academy) desde el sitio - ${email}`,
          _template: "table",
        },
        { headers: { Accept: "application/json" } }
      );
      setDone(true);
      setEmail("");
      toast.success(a.success);
    } catch (err) {
      toast.error(a.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="academy"
      data-testid="academy-section"
      className="py-24 sm:py-32 bg-[#0a0a0a] border-y border-white/5"
    >
      <div className="container-x">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img
              src={IMAGES.academy}
              alt="Angel Cars Academy"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/60" />
            <div className="relative z-10 p-8 sm:p-14 max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-light">
                <GraduationCap className="h-4 w-4" />
                {a.badge}
              </span>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
                {a.title}
              </h2>
              <p className="mt-4 text-base text-zinc-300 leading-relaxed">
                {a.text}
              </p>

              {done ? (
                <div
                  data-testid="academy-success"
                  className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-brand/40 bg-brand/10 px-5 py-4 text-white"
                >
                  <CheckCircle2 className="h-6 w-6 text-brand" />
                  <span className="font-medium">{a.success}</span>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  noValidate
                  data-testid="academy-form"
                  className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={a.placeholder}
                    data-testid="academy-email-input"
                    className="flex-1 h-13 min-h-[52px] rounded-full bg-white/5 border border-white/15 px-5 text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="academy-submit-btn"
                    className="inline-flex items-center justify-center gap-2 min-h-[52px] py-3 px-6 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold text-sm sm:text-base transition-colors disabled:opacity-60 text-center leading-tight"
                  >
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {a.button}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
